import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import {
  customerServiceTimingView,
  formatCurrency,
  formatTimerSeconds,
} from '@/lib/serviceTimer';
import useServiceStatus from '@/hooks/useServiceStatus';
import CustomerCancelBooking from '@/components/booking/CustomerCancelBooking';
import ReplacementProviderBanner from '@/components/booking/ReplacementProviderBanner';
import {
  bookingStatusLabel,
  caseTypeDisplay,
  durationDisplay,
  formatBookingDate,
} from '@/lib/bookingHistory';

export default function ConsumerBookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        setBooking(data.booking);
      } catch {
        navigate('/consumer/bookings');
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId, navigate]);

  const serviceEnabled =
    booking?.status === 'confirmed' ||
    booking?.status === 'in_progress' ||
    booking?.status === 'completed';
  const { service, timer } = useServiceStatus(bookingId, serviceEnabled);
  const arrived =
    booking?.status === 'confirmed' && service?.travel_status === 'arrived';

  // Live service timing derived purely from authoritative timestamps
  // (service_started_at / service_scheduled_end_at / service_ended_at /
  // completed_at) plus the existing server-anchored tick — never a local
  // elapsed counter, so it reconstructs correctly after reload/restart.
  const serviceTiming = booking
    ? customerServiceTimingView({
        status: booking.status,
        serviceStartedAt: booking.service_started_at,
        serviceEndedAt: booking.service_ended_at,
        completedAt: booking.completed_at,
        service,
        timer,
      })
    : null;

  if (loading) {
    return (
      <MobileShell title="Booking">
        <div className="p-5 text-slate-500 text-sm">Loading…</div>
      </MobileShell>
    );
  }

  if (!booking) return null;

  const currency = booking.quote?.currency || 'INR';
  const duration = durationDisplay(booking);
  const caseLabel = caseTypeDisplay(booking.booking_requirements);
  const canTrack =
    booking.status === 'confirmed' &&
    (booking.travel_status === 'en_route' || booking.travel_status === 'arrived');

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen">
        <MobileShell title="Booking detail">
          <div className="px-5 py-5 space-y-4" data-testid="consumer-booking-detail">
            <div className="resqly-card p-4 space-y-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Service</div>
              <div className="font-semibold">{booking.service_key?.replace(/_/g, ' ')}</div>
              {duration && <div data-testid="detail-duration">{duration}</div>}
              {caseLabel && <div data-testid="detail-case-type">{caseLabel}</div>}
            </div>

            {booking.provider_name && (
              <div className="resqly-card p-4 space-y-1">
                <div className="text-xs font-semibold text-slate-500 uppercase">Provider</div>
                <div data-testid="detail-provider">{booking.provider_name}</div>
              </div>
            )}

            <div className="resqly-card p-4 space-y-1">
              <div className="text-xs font-semibold text-slate-500 uppercase">Booking</div>
              <div className="text-xs text-slate-500">ID: {booking.id}</div>
              <div>Created: {formatBookingDate(booking.created_at)}</div>
              {booking.service_started_at && (
                <div>Service started: {formatBookingDate(booking.service_started_at)}</div>
              )}
              <div className="font-medium text-blue-700" data-testid="detail-status">
                {bookingStatusLabel(booking)}
              </div>
            </div>

            {arrived && service?.start_otp && (
              <div className="resqly-card p-4 space-y-2" data-testid="consumer-start-otp">
                <div className="text-sm font-semibold text-slate-900">Your provider has arrived</div>
                <div className="text-xs text-slate-500">Service Start OTP</div>
                <div className="text-2xl font-bold tracking-[0.3em] text-slate-900">
                  {service.start_otp}
                </div>
                <div className="text-xs text-slate-500">
                  Share this OTP with your provider to start the service.
                </div>
              </div>
            )}

            {booking.service_address && (
              <div className="resqly-card p-4 space-y-1">
                <div className="text-xs font-semibold text-slate-500 uppercase">Location</div>
                <div data-testid="detail-address">{booking.service_address}</div>
              </div>
            )}

            {booking.quote && (
              <div className="resqly-card p-4 space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase">Pricing</div>
                {(booking.quote.line_items || []).map((item) => (
                  <div key={item.key} className="flex justify-between text-sm">
                    <span>{item.label}</span>
                    <span>{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Customer total</span>
                  <span data-testid="detail-customer-total">
                    {formatCurrency(booking.quote.customer_total, currency)}
                  </span>
                </div>
              </div>
            )}

            <div className="resqly-card p-4 space-y-2">
              <div className="text-xs font-semibold text-slate-500 uppercase">Payments</div>
              <div className="text-sm">
                Base payment: {booking.payment?.status || '—'}
                {booking.payment?.amount != null && (
                  <span> ({formatCurrency(booking.payment.amount, currency)})</span>
                )}
              </div>
              {booking.extra_payment && (
                <div className="text-sm" data-testid="detail-extra-payment">
                  Extra payment: {booking.extra_payment.status}
                  {booking.extra_payment.amount != null && (
                    <span> ({formatCurrency(booking.extra_payment.amount, currency)})</span>
                  )}
                </div>
              )}
              {booking.extra_settlement?.final_extra_customer_amount != null && (
                <div className="text-sm">
                  Extra service total:{' '}
                  {formatCurrency(booking.extra_settlement.final_extra_customer_amount, currency)}
                </div>
              )}
            </div>

            {(serviceEnabled || booking.service_started_at) && (
              <div className="resqly-card p-4 space-y-1" data-testid="detail-service-timing">
                <div className="text-xs font-semibold text-slate-500 uppercase">Service Timing</div>
                {serviceTiming?.state === 'running' && serviceTiming.displaySeconds != null && (
                  <div
                    className="text-2xl font-bold text-blue-700 tabular-nums"
                    data-testid="detail-service-timer-live"
                  >
                    {formatTimerSeconds(serviceTiming.displaySeconds)}
                  </div>
                )}
                {serviceTiming?.state === 'running' && serviceTiming.displaySeconds == null && (
                  <div className="text-sm text-slate-600">Service in progress…</div>
                )}
                {serviceTiming?.state === 'finished' && (
                  <div
                    className="text-2xl font-bold text-slate-900 tabular-nums"
                    data-testid="detail-service-timer-final"
                  >
                    {formatTimerSeconds(serviceTiming.displaySeconds)}
                  </div>
                )}
                {serviceTiming?.state === 'not_started' && (
                  <div className="text-sm text-slate-600" data-testid="detail-service-timer-pending">
                    Service has not started yet
                  </div>
                )}
                {booking.service_scheduled_end_at && (
                  <div className="text-xs text-slate-500">Scheduled end: {formatBookingDate(booking.service_scheduled_end_at)}</div>
                )}
                {booking.service_ended_at && (
                  <div className="text-xs text-slate-500">Ended: {formatBookingDate(booking.service_ended_at)}</div>
                )}
                {booking.completed_at && (
                  <div className="text-xs text-slate-500">Completed: {formatBookingDate(booking.completed_at)}</div>
                )}
              </div>
            )}

            {booking.review_available && (
              <div className="text-xs text-slate-500">Review available after completion.</div>
            )}

            {booking.replacement_entitlement?.available && (
              <ReplacementProviderBanner booking={booking} />
            )}

            {booking.provider_phone && booking.status !== 'completed' && booking.status !== 'cancelled' && !booking.service_ended_at && (
              <PhoneContact
                bookingId={booking.id}
                phone={booking.provider_phone}
                contactedAt={booking.phone_contacted_at}
                onContacted={(at) => setBooking((prev) => ({ ...prev, phone_contacted_at: at }))}
                label="Call Provider"
              />
            )}

            <div className="flex flex-col gap-2">
              <CustomerCancelBooking booking={booking} onCancelled={() => navigate('/consumer/bookings')} />
              {canTrack && (
                <Button
                  data-testid="detail-track-btn"
                  onClick={() => navigate(`/consumer/bookings/${bookingId}/tracking`)}
                >
                  Track Provider
                </Button>
              )}
              {booking.status === 'payment_pending' && (
                <Button
                  data-testid="detail-pay-base-btn"
                  onClick={() => navigate(`/consumer/bookings/${bookingId}/payment`)}
                >
                  Pay Now
                </Button>
              )}
              {booking.settlement_status === 'payment_required' && (
                <Button
                  data-testid="detail-pay-extra-btn"
                  onClick={() => navigate(`/consumer/bookings/${bookingId}/extra-payment`)}
                >
                  Pay Extra Charges
                </Button>
              )}
              <Link
                to="/consumer/bookings"
                className="text-center text-sm text-blue-700 font-medium"
                data-testid="back-to-bookings"
              >
                Back to My Bookings
              </Link>
            </div>
          </div>
        </MobileShell>
        <BottomNav />
      </div>
    </div>
  );
}


function PhoneContact({ bookingId, phone, contactedAt, onContacted, label }) {
  const [loading, setLoading] = useState(false);
  const contact = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/phone-contact`);
      onContacted?.(data.phone_contacted_at);
      window.location.href = `tel:${phone}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enable calling');
    } finally { setLoading(false); }
  };
  if (!phone) return null;
  return (
    <div className="resqly-card p-4 space-y-2">
      <div className="text-xs font-semibold text-slate-500 uppercase">Service contact</div>
      <button type="button" className="w-full text-left" onClick={contact} disabled={loading} data-testid="consumer-provider-phone">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="font-semibold text-blue-700">{loading ? 'Opening phone…' : phone}</div>
      </button>
      {!contactedAt && <div className="text-[11px] text-amber-700">Calling disables cancellation for both participants.</div>}
    </div>
  );
}
